import { formatWorkplaceLabel } from "./dataMappers";

export const RESIGN_STATUS = {
  PENDING: "pending",
  WITHDRAWN: "withdrawn",
  APPROVED: "approved",
  REJECTED: "rejected",
};

export function mapApiResignStatus(status) {
  if (!status) return RESIGN_STATUS.PENDING;
  const normalized = String(status).toLowerCase();
  if (normalized === "cancelled" || normalized === "canceled") {
    return RESIGN_STATUS.WITHDRAWN;
  }
  if (normalized === "approved" || normalized === "closed") {
    return RESIGN_STATUS.APPROVED;
  }
  if (normalized === "rejected") {
    return RESIGN_STATUS.REJECTED;
  }
  if (normalized === "pending" || normalized === "in_progress") {
    return RESIGN_STATUS.PENDING;
  }
  return RESIGN_STATUS.PENDING;
}

export function mapApiResignationForReview(apiResignation) {
  if (!apiResignation) return null;
  const employee = apiResignation.employee || {};
  return {
    id: apiResignation.id,
    requestId: apiResignation.requestId,
    employeeId: apiResignation.userId || employee.id,
    employeeRecordId: employee.employeeId || null,
    employeeName: employee.fullName || employee.username || "موظف",
    branchName: employee.branch?.name || "",
    departmentName: employee.department?.name || "",
    workplaceLabel: formatWorkplaceLabel(
      employee.branch?.name,
      employee.department?.name,
    ),
    employeeRole: null,
    managerId: employee.managerId || null,
    departmentId: employee.departmentId || null,
    departmentManagerId: null,
    lastWorkingDay: apiResignation.lastWorkingDay,
    reason: apiResignation.reason || null,
    handoverNotes: apiResignation.handoverNotes || null,
    fileNames: [],
    submittedAt: apiResignation.createdAt,
    status: mapApiResignStatus(apiResignation.status),
    currentStep: apiResignation.currentStep,
    withdrawalDeadline: apiResignation.withdrawalDeadline,
    reviewedBy: null,
    reviewedAt: null,
    reviewNotes: null,
  };
}

export function canViewResignReason(role) {
  return (
    role === "department-manager" ||
    role === "hr" ||
    role === "hr-staff" ||
    role === "gm" ||
    role === "owner"
  );
}

export function canUpdateResignStatus(role) {
  return role === "hr" || role === "hr-staff";
}

export function isPendingResignationStatus(status) {
  const normalized = String(status || "").toLowerCase();
  return (
    normalized === RESIGN_STATUS.PENDING ||
    normalized === "in_progress"
  );
}

/** HR / hr-staff only — sole approvers for resignation requests. */
export function canUserApproveResignation(resignation, user) {
  if (!resignation || !user || !canUpdateResignStatus(user.role)) {
    return false;
  }
  return isPendingResignationStatus(resignation.status);
}

/** Pending resignations the current user can approve (HR actionable inbox). */
export function countPendingResignationsForUser(resignations = [], user) {
  if (!user || !canUpdateResignStatus(user.role)) return 0;
  return (resignations || []).filter(
    (r) => canUserApproveResignation(r, user),
  ).length;
}

export function getResignStatusLabel(status) {
  const labels = {
    [RESIGN_STATUS.PENDING]: "قيد المراجعة",
    [RESIGN_STATUS.WITHDRAWN]: "مسحوب",
    [RESIGN_STATUS.APPROVED]: "مقبول",
    [RESIGN_STATUS.REJECTED]: "مرفوض",
  };
  return labels[status] || status;
}

/** إخفاء سبب الاستقالة عن غير مدير الإدارة و HR */
export function sanitizeResignationForViewer(resignation, viewerRole) {
  if (!resignation) return null;
  if (canViewResignReason(viewerRole)) return resignation;
  const { reason, ...rest } = resignation;
  return rest;
}

/**
 * Returns true when `viewerEmployeeId` appears anywhere above `employeeId`
 * in the Employee.managerId reporting chain.
 */
export function isInManagerHierarchy(employeeId, viewerEmployeeId, getEmployeeById) {
  if (!employeeId || !viewerEmployeeId || !getEmployeeById) return false;
  if (String(employeeId) === String(viewerEmployeeId)) return false;

  let current = getEmployeeById(employeeId);
  const visited = new Set();

  while (current?.managerId) {
    const managerId = String(current.managerId);
    if (visited.has(managerId)) break;
    visited.add(managerId);

    if (managerId === String(viewerEmployeeId)) return true;
    current = getEmployeeById(current.managerId);
  }

  return false;
}

/** Collect manager employee IDs from direct manager up the chain. */
export function getManagerChainEmployeeIds(employeeId, getEmployeeById) {
  if (!employeeId || !getEmployeeById) return [];

  const ids = [];
  let current = getEmployeeById(employeeId);
  const visited = new Set();

  while (current?.managerId) {
    const managerId = String(current.managerId);
    if (visited.has(managerId)) break;
    visited.add(managerId);
    ids.push(managerId);
    current = getEmployeeById(current.managerId);
  }

  return ids;
}

export function getResignationsForRole(
  resignations,
  userRole,
  userId,
  getEmployeeById,
  viewerEmployeeId = null,
) {
  if (userRole === "hr" || userRole === "hr-staff" || userRole === "gm") {
    return [...resignations].sort(
      (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt),
    );
  }

  if (userRole === "department-manager") {
    return resignations
      .filter((r) => {
        if (r.departmentManagerId === userId) return true;
        const emp =
          getEmployeeById(r.employeeRecordId) ||
          getEmployeeById(r.employeeId);
        if (emp?.departmentManagerId === userId) return true;
        if (
          viewerEmployeeId &&
          isInManagerHierarchy(
            emp?.id || r.employeeRecordId,
            viewerEmployeeId,
            getEmployeeById,
          )
        ) {
          return true;
        }
        return false;
      })
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  }

  if (userRole === "manager") {
    return resignations
      .filter((r) => {
        if (r.employeeId === userId) return false;
        if (
          Array.isArray(r.managerChainIds) &&
          viewerEmployeeId &&
          r.managerChainIds.some((id) => String(id) === String(viewerEmployeeId))
        ) {
          return true;
        }
        if (
          r.managerId &&
          viewerEmployeeId &&
          String(r.managerId) === String(viewerEmployeeId)
        ) {
          return true;
        }
        const emp =
          getEmployeeById(r.employeeRecordId) ||
          getEmployeeById(r.employeeId);
        if (!emp || !viewerEmployeeId) return false;
        return isInManagerHierarchy(emp.id, viewerEmployeeId, getEmployeeById);
      })
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  }

  if (userRole === "employee") {
    return resignations
      .filter((r) => r.employeeId === userId)
      .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
  }

  return [];
}

export function getActiveResignationForEmployee(resignations, employeeId) {
  return resignations.find(
    (r) =>
      r.employeeId === employeeId &&
      r.status === RESIGN_STATUS.PENDING,
  );
}

export function canWithdrawResignation(resignation) {
  if (!resignation || resignation.status !== RESIGN_STATUS.PENDING) {
    return false;
  }
  const today = new Date();
  const submittedAt = new Date(resignation.submittedAt);
  const diffDays = Math.ceil(
    (today.getTime() - submittedAt.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diffDays <= 7;
}

export function getWithdrawDaysRemaining(resignation) {
  const today = new Date();
  const submittedAt = new Date(resignation.submittedAt);
  const diffDays = Math.ceil(
    (today.getTime() - submittedAt.getTime()) / (1000 * 60 * 60 * 24),
  );
  return Math.max(0, 7 - diffDays);
}

export function getPendingResignationsCount(
  resignations,
  userRole,
  userId,
  getEmployeeById,
  viewerEmployeeId = null,
) {
  return getResignationsForRole(
    resignations,
    userRole,
    userId,
    getEmployeeById,
    viewerEmployeeId,
  ).filter((r) => isPendingResignationStatus(r.status)).length;
}
